import { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { grey } from "@mui/material/colors";
import { Add, ArrowBack, Close } from "@mui/icons-material";
import { nanoid } from "nanoid";
import { useSnackbar } from "notistack";
import { useHistory, useRouteMatch } from "@/compat/router";
import { useAppSelector } from "@/redux/hooks";
import { KeyboardDatePicker } from "@/compat/pickers";
import CardBase from "../../../components/cards/CardBase";
import CustomButton from "../../../components/CustomButton";
import HazardActions from "../../components/riskassessments/HazardActions";
import RiskScoreCell from "../../components/riskassessments/RiskScoreCell";
import HazardAttachments from "../../components/riskassessments/HazardAttachments";
import HazardPickerDialog from "../../components/riskassessments/HazardPickerDialog";
import PeopleAtRiskCard from "../../components/riskassessments/PeopleAtRiskCard";
import RiskAssessmentCompletedCard from "../../components/riskassessments/RiskAssessmentCompletedCard";
import HazardControlAmendDialog from "../../components/riskassessments/dialogs/HazardControlAmendDialog";
import { useRiskAssessmentDetails } from "../../contexts/RiskAssessmentContext";
import useSaveRiskAssessmentHistory from "../../hooks/mutations/useSaveRiskAssessmentHistory";
import usePeople from "../../hooks/queries/usePeople";
import useHazards from "../../hooks/queries/useHazards";
import useContactsForAppAndSite from "../../../hooks/useContactsForAppAndSite";
import moduleAreas, { moduleUrls } from "../../constants/moduleAreaConstants";
import { baseRoute } from "../../routes";
import { buildSavePayload, hazardFilesFromAttachments, validateForComplete } from "../../utils/riskAssessmentSave";
import useRiskAssessmentAttachments from "../../hooks/queries/useRiskAssessmentAttachments";

const hazardImagesUrl = `${process.env.PUBLIC_URL}/images/hazardIcons/`;
const fallbackHazardIcon = `${hazardImagesUrl}General warning.svg`;

const useStyles = makeStyles((theme) => ({
    header: { marginBottom: theme.spacing(2) },
    title: { fontFamily: ["Sailec", "Arial", "sans-serif"].join(","), fontWeight: 700 },
    subtitle: { color: theme.palette.text.secondary },
    section: { marginBottom: theme.spacing(2) },
    progressRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: theme.spacing(2), marginBottom: theme.spacing(1) },
    progressBar: { height: 8, borderRadius: 4 },
    guidance: {
        backgroundColor: theme.palette.mode === "dark" ? grey[900] : "#f4f1ff",
        border: `1px solid ${theme.palette.mode === "dark" ? grey[800] : "#e5defb"}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
    },
    overallItem: { display: "flex", flexDirection: "column" },
    overallLabel: { fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.5, color: theme.palette.text.secondary },
    overallValue: { fontWeight: 600 },
    tableWrap: { overflowX: "auto" },
    table: { minWidth: 1040 },
    headCell: { fontWeight: 700, whiteSpace: "nowrap" },
    hazardCell: { display: "flex", alignItems: "center", gap: theme.spacing(1), minWidth: 150 },
    hazardIcon: { height: 28, width: 28, objectFit: "contain", flexShrink: 0 },
    controlsCol: { minWidth: 400, width: "55%" },
    ctrlItem: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    bullet: { color: theme.palette.text.secondary, fontSize: "0.85rem", lineHeight: 1.7, flexShrink: 0 },
    yesNo: { display: "flex", gap: theme.spacing(0.5), flexShrink: 0 },
    yesNoBtn: { minWidth: 46, textTransform: "none", paddingTop: 2, paddingBottom: 2 },
    ctrlName: {
        flex: 1,
        fontSize: "0.85rem",
        cursor: "pointer",
        "&:hover": { color: theme.palette.secondary.main, textDecoration: "underline" },
    },
    addCtrlRow: { display: "flex", alignItems: "center", gap: theme.spacing(0.5), marginTop: theme.spacing(1) },
    addControlLink: { textTransform: "none", color: theme.palette.text.secondary, marginTop: theme.spacing(0.5) },
    addHazardBtn: { marginTop: theme.spacing(2), borderStyle: "dashed", paddingTop: theme.spacing(1.5), paddingBottom: theme.spacing(1.5) },
    emptyHazards: { color: theme.palette.text.secondary, padding: theme.spacing(3), textAlign: "center" },
    muted: { color: theme.palette.text.secondary },
    footerBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    spinnerWrap: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
}));

export default function CompleteRiskAssessment() {
    const classes = useStyles();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const match = useRouteMatch();
    const { riskAssessmentId } = match.params;

    const { sites } = useAppSelector((state) => state.account);

    const {
        riskAssessment,
        riskAssessmentDetails,
        riskAssessmentHistories,
        setSelectedRiskAssessmentId,
        setSelectedSiteId,
        selectedSiteId,
        isEditMode,
        addPreControlSeverityScore,
        addPreControlLikelihoodScore,
        addPostControlSeverityScore,
        addPostControlLikelihoodScore,
        toggleControl,
        addControl,
        removeControl,
        updateControlText,
        addHazard,
        removeHazard,
        addCustomHazard,
        removeCustomHazard,
        addPerson,
        removePerson,
        addCustomPerson,
        removeCustomPerson,
        nextDueDate,
        setNextDueDate,
        requiresDueDate,
        reset,
    } = useRiskAssessmentDetails();

    const [controlDrafts, setControlDrafts] = useState<Record<string, string>>({});
    const [addingControlFor, setAddingControlFor] = useState<string | null>(null);
    const [hazardPickerOpen, setHazardPickerOpen] = useState(false);
    const [controlAmendOpen, setControlAmendOpen] = useState(false);
    const [controlToAmend, setControlToAmend] = useState<any>(null);
    const [hazardKeyToAmend, setHazardKeyToAmend] = useState<any>(null);
    const [isCustomToAmend, setIsCustomToAmend] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<{ key: string; name: string; isCustom: boolean } | null>(null);
    // azureFileIds picked per hazard while completing, pinned to the hazard rows on save.
    const [hazardFiles, setHazardFiles] = useState<Record<string, number[]>>({});
    const hazardFilesSeeded = useRef(false);
    const [completed, setCompleted] = useState<{ hazardsAssessed: number; controlsInPlace: number; actionsAssigned: number } | null>(null);

    const attachments = useRiskAssessmentAttachments(riskAssessmentId);
    const saveRiskAssessmentHistory = useSaveRiskAssessmentHistory();
    const allPeople = usePeople();
    const allHazardsLibrary = useHazards();
    const contacts = useContactsForAppAndSite({ selectedSiteId });

    const raListRoute = `${baseRoute}/${moduleUrls[moduleAreas.RISKASSESSMENTS]}`;
    const raData = riskAssessment?.data;
    const raInfo = riskAssessmentHistories?.data?.riskAssessment;

    useEffect(() => {
        if (riskAssessmentId) setSelectedRiskAssessmentId(Number(riskAssessmentId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskAssessmentId]);

    useEffect(() => {
        if (raData?.siteExternalId) setSelectedSiteId(raData.siteExternalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raData?.siteExternalId]);

    // Seeded once: a refetch would drop ids picked since, which upload at assessment
    // level and only reach their hazard on save.
    useEffect(() => {
        const hazardRows = raData?.riskAssessmentHistory?.riskAssessmentHazards;

        if (!isEditMode || hazardFilesSeeded.current || !hazardRows || !Array.isArray(attachments?.data)) return;

        hazardFilesSeeded.current = true;
        setHazardFiles(hazardFilesFromAttachments(hazardRows, attachments.data));
    }, [isEditMode, raData?.riskAssessmentHistory, attachments?.data]);

    const seeded = !!raData && riskAssessmentDetails?.id === raData?.id;

    const hazards = riskAssessmentDetails?.hazards ?? {};
    const customHazards = riskAssessmentDetails?.customHazards ?? {};
    const allHazardKeys = useMemo(
        () => [...Object.keys(hazards), ...Object.keys(customHazards)],
        [hazards, customHazards]
    );

    const employee = contacts?.data?.[raInfo?.assignedEmployeeExternalId];
    const overallInfo = [
        { label: "Site", value: sites?.[raInfo?.siteExternalId]?.name ?? "—" },
        { label: "Responsible employee", value: employee ? `${employee.firstName} ${employee.lastName}` : "—" },
        { label: "Review frequency", value: raInfo?.frequencyDescription ?? "—" },
        { label: "Due date", value: raInfo?.dueDate ? new Date(raInfo.dueDate).toLocaleDateString("en-GB") : "—" },
    ];

    // Completion progress: a hazard is "done" when both risk scores are set and every control is answered.
    const completeHazards = useMemo(
        () =>
            allHazardKeys.filter((key) => {
                const h = (customHazards as any)[key] ?? (hazards as any)[key];
                const scored =
                    h?.severityScorePreControl > 0 &&
                    h?.likelihoodScorePreControl > 0 &&
                    h?.severityScorePostControl > 0 &&
                    h?.likelihoodScorePostControl > 0;
                const controlsAnswered = (h?.hazardControls ?? []).every((c: any) => c?.inPlace !== undefined);
                return scored && controlsAnswered;
            }).length,
        [allHazardKeys, hazards, customHazards]
    );
    const totalHazards = allHazardKeys.length;
    const progressPct = totalHazards ? Math.round((completeHazards / totalHazards) * 100) : 0;

    const currentPeopleIds = Object.keys(riskAssessmentDetails?.people ?? {}).map(Number);
    const currentCustomPeople = Object.values(riskAssessmentDetails?.customPeople ?? {}).map((p: any) => ({
        id: p.id,
        name: p.name,
    }));
    const currentHazardIds = Object.keys(hazards).map(Number);
    const currentCustomHazards = Object.values(customHazards).map((c: any) => ({ id: c.id, name: c.name }));

    // User-added controls carry nanoid string ids (removable); seeded ones are numeric.
    const isAddedControl = (c: any) => isNaN(Number(c?.id));

    function scoreTarget(key: string, isCustom: boolean) {
        return isCustom ? { customHazardId: key } : { hazardId: key };
    }

    function addControlToHazard(key: string, isCustom: boolean) {
        const name = (controlDrafts[key] ?? "").trim();
        if (!name) return;
        const id = nanoid(5);
        addControl({ hazardId: key, id, name, isCustomHazard: isCustom });
        setControlDrafts((d) => ({ ...d, [key]: "" }));
    }

    function removeAddedControl(key: string, controlId: any, isCustom: boolean) {
        removeControl({ hazardId: key, controlId, isCustomHazard: isCustom });
    }

    function openControlEdit(key: string, control: any, isCustom: boolean) {
        setControlToAmend(control);
        setHazardKeyToAmend(key);
        setIsCustomToAmend(isCustom);
        setControlAmendOpen(true);
    }

    function handleAmendControl(payload: any) {
        updateControlText({
            hazardIdToAmend: hazardKeyToAmend,
            controlId: payload.controlId,
            hazardControlText: payload.hazardControlText,
            isCustomHazard: isCustomToAmend,
            isCustomControl: payload.isCustomControl,
        });
    }

    function confirmRemoveHazard() {
        if (!removeTarget) return;
        if (removeTarget.isCustom) removeCustomHazard(removeTarget.key);
        else removeHazard(Number(removeTarget.key));
        setRemoveTarget(null);
    }

    function renderControlRow(control: any, key: string, isCustom: boolean) {
        return (
            <div className={classes.ctrlItem} key={control?.id}>
                <span className={classes.bullet}>•</span>
                <Typography
                    component="span"
                    className={classes.ctrlName}
                    title="Click to edit"
                    onClick={() => openControlEdit(key, control, isCustom)}
                >
                    {control?.control?.name}
                </Typography>
                <div className={classes.yesNo}>
                    <Button
                        size="small"
                        className={classes.yesNoBtn}
                        variant={control?.inPlace === true ? "outlined" : "text"}
                        color="primary"
                        onClick={() => toggleControl({ hazardId: key, controlId: control?.id, inPlace: true, isCustomHazard: isCustom })}
                    >
                        Yes
                    </Button>
                    <Button
                        size="small"
                        className={classes.yesNoBtn}
                        variant={control?.inPlace === false ? "contained" : "text"}
                        color="error"
                        onClick={() => toggleControl({ hazardId: key, controlId: control?.id, inPlace: false, isCustomHazard: isCustom })}
                    >
                        No
                    </Button>
                </div>
                {isAddedControl(control) && (
                    <IconButton
                        size="small"
                        aria-label={`Remove control ${control?.control?.name}`}
                        onClick={() => removeAddedControl(key, control?.id, isCustom)}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                )}
            </div>
        );
    }

    // Reconcile the picker's draft selection against the reducer's current hazards/people.
    function reconcileHazards({ hazardIds, customHazards: pickedCustom }: { hazardIds: any[]; customHazards: any[] }) {
        const currentLib = Object.keys(hazards);
        const selectedLib = (hazardIds ?? []).map(String);
        selectedLib
            .filter((id) => !currentLib.includes(id))
            .forEach((id) => {
                const full = (allHazardsLibrary?.data ?? []).find((h: any) => String(h.id) === id);
                if (full) addHazard(full);
            });
        currentLib.filter((id) => !selectedLib.includes(id)).forEach((id) => removeHazard(Number(id)));

        const currentCustom = Object.keys(customHazards);
        const selectedCustom = pickedCustom ?? [];
        selectedCustom
            .filter((c) => !currentCustom.includes(String(c.id)))
            .forEach((c) => addCustomHazard({ id: c.id, name: c.name }));
        currentCustom
            .filter((id) => !selectedCustom.some((c) => String(c.id) === id))
            .forEach((id) => removeCustomHazard(id));
    }

    function reconcilePeople({ peopleIds, customPeople: pickedCustom }: { peopleIds: any[]; customPeople: any[] }) {
        const people = riskAssessmentDetails?.people ?? {};
        const customPeople = riskAssessmentDetails?.customPeople ?? {};
        const currentLib = Object.keys(people);
        const selectedLib = (peopleIds ?? []).map(String);
        selectedLib
            .filter((id) => !currentLib.includes(id))
            .forEach((id) => {
                const full = (allPeople?.data ?? []).find((p: any) => String(p.id) === id);
                addPerson({ id: full?.id ?? Number(id), name: full?.name });
            });
        currentLib.filter((id) => !selectedLib.includes(id)).forEach((id) => removePerson(Number(id)));

        const currentCustom = Object.keys(customPeople);
        const selectedCustom = pickedCustom ?? [];
        selectedCustom
            .filter((c) => !currentCustom.includes(String(c.id)))
            .forEach((c) => addCustomPerson({ id: c.id, name: c.name }));
        currentCustom
            .filter((id) => !selectedCustom.some((c) => String(c.id) === id))
            .forEach((id) => removeCustomPerson(id));
    }

    function onComplete() {
        const error = validateForComplete(riskAssessmentDetails, { requiresDueDate, nextDueDate, isEditMode });
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            return;
        }
        const allControls = allHazardKeys.flatMap(
            (k) => ((customHazards as any)[k] ?? (hazards as any)[k])?.hazardControls ?? []
        );
        const summary = {
            hazardsAssessed: allHazardKeys.length,
            controlsInPlace: allControls.filter((c: any) => c?.inPlace === true).length,
            actionsAssigned: allControls.filter((c: any) => c?.inPlace === false).length,
        };
        // Clone first: buildSavePayload mutates its input in place, and on a failed save we stay on
        // the page — mutating live reducer state would corrupt a retry and the table re-render.
        const payload = buildSavePayload(structuredClone(riskAssessmentDetails), {
            hazardFiles,
            nextDueDate,
            isEditMode,
            riskAssessmentHistoryId: raData?.riskAssessmentHistory?.id,
            allPeople: allPeople?.data,
        });
        saveRiskAssessmentHistory.mutate(payload, {
            onSuccess: () => {
                riskAssessmentHistories?.refetch?.();
                setCompleted(summary);
            },
            onError: (error) => {
                console.error(error);
                enqueueSnackbar("Could not save risk assessment.", { variant: "error" });
            },
        });
    }

    function onBack() {
        reset();
        history.goBack();
    }

    function leaveTo(path: string) {
        reset();
        history.push(path);
    }

    if (completed) {
        const viewPath = `${raListRoute}/view/${riskAssessmentId}`;
        return (
            <RiskAssessmentCompletedCard
                heading={`${raInfo?.templateName ?? "Risk assessment"} completed!`}
                subtitle="Well done — your risk assessment has been saved to the register and review reminders have been scheduled."
                stats={[
                    { n: completed.hazardsAssessed, l: "Hazards assessed" },
                    { n: completed.controlsInPlace, l: "Controls in place" },
                    { n: completed.actionsAssigned, l: "Actions assigned" },
                ]}
                onView={() => leaveTo(viewPath)}
                onShare={() => leaveTo(`${viewPath}?share=1`)}
                onDownload={() => leaveTo(viewPath)}
                onBack={() => leaveTo(raListRoute)}
                backLabel="Back to Risk Assessments List"
            />
        );
    }

    return (
        <Container>
            {!seeded ? (
                <div className={classes.spinnerWrap}>
                    {riskAssessment?.isError ? (
                        <div style={{ textAlign: "center" }}>
                            <Typography color="error" gutterBottom>
                                Couldn't load this risk assessment.
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                It may have been removed, or your session may have expired — try refreshing the page.
                            </Typography>
                            <Button variant="outlined" color="primary" startIcon={<ArrowBack />} onClick={onBack} sx={{ mt: 2 }}>
                                Back
                            </Button>
                        </div>
                    ) : (
                        <CircularProgress color="primary" />
                    )}
                </div>
            ) : (
                <>
                    <div className={classes.header}>
                        <Typography variant="h5" className={classes.title}>
                            {raInfo?.templateName ?? "Risk Assessment"}
                        </Typography>
                        <Typography variant="body2" className={classes.subtitle}>
                            {isEditMode ? "Edit assessment" : "Complete assessment"}
                        </Typography>
                    </div>

                    <div className={classes.section}>
                        <CardBase>
                            <div className={classes.progressRow}>
                                <Typography variant="subtitle2">Assessment progress</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {totalHazards === 0
                                        ? "Add a hazard to begin"
                                        : `${completeHazards} of ${totalHazards} hazard${totalHazards === 1 ? "" : "s"} complete`}
                                </Typography>
                            </div>
                            <LinearProgress variant="determinate" value={progressPct} className={classes.progressBar} />
                        </CardBase>
                    </div>

                    <div className={classes.section}>
                        <CardBase flat>
                            <div className={classes.guidance}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Complete the assessment
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    For each hazard, score the initial risk, mark each control Yes (in place) or No (a "No" is
                                    listed as not in place and creates an action below), then score the residual risk.
                                </Typography>
                            </div>
                        </CardBase>
                    </div>

                    <div className={classes.section}>
                        <CardBase title="Overall info">
                            <Grid container spacing={2}>
                                {overallInfo.map((item) => (
                                    <Grid size={{ xs: 6, md: 3 }} key={item.label}>
                                        <div className={classes.overallItem}>
                                            <Typography className={classes.overallLabel}>{item.label}</Typography>
                                            <Typography className={classes.overallValue}>{item.value}</Typography>
                                        </div>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardBase>
                    </div>

                    <div className={classes.section}>
                        <PeopleAtRiskCard
                            people={allPeople?.data ?? []}
                            selectedPeopleIds={currentPeopleIds}
                            customPeople={currentCustomPeople}
                            siteExternalId={raData?.siteExternalId ?? ""}
                            onSave={reconcilePeople}
                        />
                    </div>

                    <div className={classes.section}>
                        <CardBase title="Hazards & Risks">
                            {totalHazards === 0 ? (
                                <Typography className={classes.emptyHazards}>
                                    No hazards yet — add one to start the assessment.
                                </Typography>
                            ) : (
                                <div className={classes.tableWrap}>
                                    <Table className={classes.table} size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.headCell}>#</TableCell>
                                                <TableCell className={classes.headCell}>Hazard</TableCell>
                                                <TableCell className={classes.headCell}>Initial risk</TableCell>
                                                <TableCell className={classes.headCell}>Controls</TableCell>
                                                <TableCell className={classes.headCell}>Residual risk</TableCell>
                                                <TableCell className={classes.headCell}>Files</TableCell>
                                                <TableCell className={classes.headCell} />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {allHazardKeys.map((key, index) => {
                                                const isCustom = Object.keys(customHazards).includes(key);
                                                const hazard = isCustom ? (customHazards as any)[key] : (hazards as any)[key];
                                                const controlList = hazard?.hazardControls ?? [];
                                                return (
                                                    <TableRow key={key} sx={{ verticalAlign: "top" }}>
                                                        <TableCell>{index + 1}</TableCell>
                                                        <TableCell>
                                                            <div className={classes.hazardCell}>
                                                                <img
                                                                    className={classes.hazardIcon}
                                                                    alt=""
                                                                    src={`${hazardImagesUrl}${hazard?.name}.svg`}
                                                                    onError={(e) => {
                                                                        (e.currentTarget as HTMLImageElement).src = fallbackHazardIcon;
                                                                    }}
                                                                />
                                                                <Typography variant="body2">{hazard?.name}</Typography>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <RiskScoreCell
                                                                ariaLabel={`Initial risk for ${hazard?.name}`}
                                                                severity={hazard?.severityScorePreControl ?? 0}
                                                                likelihood={hazard?.likelihoodScorePreControl ?? 0}
                                                                onSeverityChange={(v) =>
                                                                    addPreControlSeverityScore({ ...scoreTarget(key, isCustom), value: v })
                                                                }
                                                                onLikelihoodChange={(v) =>
                                                                    addPreControlLikelihoodScore({ ...scoreTarget(key, isCustom), value: v })
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell className={classes.controlsCol}>
                                                            {controlList.length === 0 ? (
                                                                <Typography variant="caption" className={classes.muted}>
                                                                    No controls
                                                                </Typography>
                                                            ) : (
                                                                controlList.map((control: any) => renderControlRow(control, key, isCustom))
                                                            )}
                                                            {addingControlFor === key ? (
                                                                <div className={classes.addCtrlRow}>
                                                                    <TextField
                                                                        autoFocus
                                                                        fullWidth
                                                                        size="small"
                                                                        variant="outlined"
                                                                        placeholder="Control measure…"
                                                                        value={controlDrafts[key] ?? ""}
                                                                        onChange={(e) =>
                                                                            setControlDrafts((d) => ({ ...d, [key]: e.target.value }))
                                                                        }
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter") {
                                                                                e.preventDefault();
                                                                                addControlToHazard(key, isCustom);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="secondary"
                                                                        disabled={!(controlDrafts[key] ?? "").trim()}
                                                                        onClick={() => addControlToHazard(key, isCustom)}
                                                                    >
                                                                        Add
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setAddingControlFor(null);
                                                                            setControlDrafts((d) => ({ ...d, [key]: "" }));
                                                                        }}
                                                                    >
                                                                        Done
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<Add fontSize="small" />}
                                                                    className={classes.addControlLink}
                                                                    onClick={() => setAddingControlFor(key)}
                                                                >
                                                                    Add control
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <RiskScoreCell
                                                                ariaLabel={`Residual risk for ${hazard?.name}`}
                                                                severity={hazard?.severityScorePostControl ?? 0}
                                                                likelihood={hazard?.likelihoodScorePostControl ?? 0}
                                                                onSeverityChange={(v) =>
                                                                    addPostControlSeverityScore({ ...scoreTarget(key, isCustom), value: v })
                                                                }
                                                                onLikelihoodChange={(v) =>
                                                                    addPostControlLikelihoodScore({ ...scoreTarget(key, isCustom), value: v })
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <HazardAttachments
                                                                riskAssessmentId={riskAssessmentDetails?.id}
                                                                azureFileIds={hazardFiles[key] ?? []}
                                                                onChange={(ids) =>
                                                                    setHazardFiles((prev) => ({ ...prev, [key]: ids }))
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={`Remove ${hazard?.name}`}
                                                                onClick={() => setRemoveTarget({ key, name: hazard?.name, isCustom })}
                                                            >
                                                                <Close fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            <Button
                                fullWidth
                                variant="outlined"
                                color="secondary"
                                startIcon={<Add />}
                                className={classes.addHazardBtn}
                                onClick={() => setHazardPickerOpen(true)}
                            >
                                Add hazard
                            </Button>
                        </CardBase>
                    </div>

                    <div className={classes.section}>
                        <CardBase title="Action plan" description="Auto-populated from controls that are not in place.">
                            <HazardActions />
                        </CardBase>
                    </div>

                    {requiresDueDate && (
                        <div className={classes.section}>
                            <CardBase title="Next due date">
                                <KeyboardDatePicker
                                    format="dd/MM/yyyy"
                                    inputVariant="outlined"
                                    placeholder="Next due date"
                                    value={nextDueDate}
                                    minDate={new Date()}
                                    onChange={(date) => setNextDueDate(date)}
                                />
                            </CardBase>
                        </div>
                    )}

                    <div className={classes.footerBar}>
                        <Button variant="outlined" color="primary" startIcon={<ArrowBack />} onClick={onBack}>
                            Back
                        </Button>
                        <CustomButton
                            variant="contained"
                            color="primary"
                            isLoading={saveRiskAssessmentHistory.isLoading}
                            onClick={onComplete}
                        >
                            Complete
                        </CustomButton>
                    </div>
                </>
            )}

            <HazardPickerDialog
                open={hazardPickerOpen}
                onClose={() => setHazardPickerOpen(false)}
                hazards={allHazardsLibrary?.data ?? []}
                selectedHazardIds={currentHazardIds}
                customHazards={currentCustomHazards}
                siteExternalId={raData?.siteExternalId ?? ""}
                onSave={reconcileHazards}
            />
            <HazardControlAmendDialog
                isOpen={controlAmendOpen}
                setIsOpen={setControlAmendOpen}
                hazardControl={controlToAmend}
                updateControl={handleAmendControl}
            />
            <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Remove hazard?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        "{removeTarget?.name}" will be removed from this assessment, along with any scores and controls you've
                        entered for it.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveTarget(null)} color="primary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={confirmRemoveHazard} color="error" variant="contained">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
